'use client';

import { useRef } from 'react';
import { type DragSourceMonitor, useDrag } from 'react-dnd';
import { fieldRegistry } from '../registry';

interface FieldTypePanelProps {
  onFieldDrop: (fieldType: string) => void;
}

const FIELD_TYPES = fieldRegistry.getAll();

interface DraggableFieldTypeProps {
  type: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  description: string;
}

function DraggableFieldType({ type, label, Icon, description }: DraggableFieldTypeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'FIELD_TYPE',
      item: { fieldType: type },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [type]
  );

  // react-dnd's connector function registers the DOM node with the drag backend; it doesn't
  // dereference ref.current synchronously, so this is safe despite the compiler's conservative
  // static check (see builder-area.tsx for the fuller rationale).
  // eslint-disable-next-line react-hooks/refs
  drag(ref);

  return (
    <div
      ref={ref}
      className={`w-full flex items-start gap-3 p-4 bg-muted/30 hover:bg-accent border border-border hover:border-primary/30 rounded-lg cursor-move transition-all group shadow-sm hover:shadow-md select-none ${
        isDragging ? 'opacity-50 scale-95' : ''
      }`}
    >
      <Icon className="w-5 h-5 text-muted-foreground group-hover:text-primary flex-shrink-0 mt-0.5" />
      <div className="text-left pointer-events-none">
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}

export function FieldTypePanel({ onFieldDrop: _onFieldDrop }: FieldTypePanelProps) {
  return (
    <div className="p-5">
      <h2 className="text-lg font-bold mb-2">Field Types</h2>
      <p className="text-xs text-muted-foreground mb-5">Drag fields to the builder area →</p>
      <div className="space-y-3">
        {FIELD_TYPES.map(({ type, toolboxLabel, icon: Icon, description }) => (
          <DraggableFieldType
            key={type}
            type={type}
            label={toolboxLabel}
            Icon={Icon}
            description={description}
          />
        ))}
      </div>
    </div>
  );
}
